import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Facebook, Instagram, Linkedin } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-brand-navy border-t border-white/10" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
          {/* Logo and tagline */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center gap-2 mb-4" data-testid="footer-logo">
              <img src="https://customer-assets.emergentagent.com/job_box-truck-delivery/artifacts/i3lweui9_image.png" alt="TopFleet Truck" className="h-10 w-auto" style={{ mixBlendMode: 'lighten', transform: 'scaleX(-1)' }} />
              <span className="font-heading text-xl font-black tracking-tight">
                <span style={{ color: '#DC2626' }}>TOP</span><span className="text-white">FLEET</span>
              </span>
            </Link>
            <p className="text-slate-400 text-sm leading-relaxed mb-4">
              Driven by Reliability.<br />Delivered with Precision.
            </p>
            <p className="text-slate-600 text-xs">Real-time updates via call & text</p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading text-white text-sm tracking-wider mb-5">QUICK LINKS</h4>
            <ul className="space-y-2.5">
              {[
                { to: '/services', label: 'Services' },
                { to: '/track', label: 'Track Shipment' },
                { to: '/about', label: 'About Us' },
                { to: '/contact', label: 'Contact' },
                { to: '/partners', label: 'Partnerships' },
                { to: '/careers', label: 'Careers' },
              ].map(link => (
                <li key={link.to}>
                  <Link to={link.to} className="text-slate-400 hover:text-white transition-colors text-sm" data-testid={`footer-${link.label.toLowerCase().replace(/\s+/g, '-')}-link`}>
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-heading text-white text-sm tracking-wider mb-5">SERVICES</h4>
            <ul className="space-y-2.5">
              <li><Link to="/services#commercial-freight" className="text-slate-400 hover:text-white transition-colors text-sm">Commercial Freight</Link></li>
              <li><Link to="/services#residential-moving" className="text-slate-400 hover:text-white transition-colors text-sm">Residential Moving</Link></li>
              <li><Link to="/services#on-demand-delivery" className="text-slate-400 hover:text-white transition-colors text-sm">On-Demand Delivery</Link></li>
              <li><Link to="/legal" className="text-slate-400 hover:text-white transition-colors text-sm" data-testid="footer-legal-link">Legal & Policies</Link></li>
            </ul>
          </div>

          {/* Service Area */}
          <div>
            <h4 className="font-heading text-white text-sm tracking-wider mb-5">SERVICE AREA</h4>
            <ul className="space-y-1.5">
              {['Georgia', 'South Carolina', 'North Carolina', 'Florida', 'Alabama', 'Tennessee', 'Mississippi'].map(state => (
                <li key={state} className="text-slate-400 text-sm">{state}</li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-heading text-white text-sm tracking-wider mb-5">CONTACT</h4>
            <ul className="space-y-3">
              <li>
                <a href="tel:224-374-7400" className="flex items-center gap-3 text-slate-400 hover:text-white transition-colors group" data-testid="footer-phone">
                  <Phone className="w-4 h-4 text-brand-red group-hover:scale-110 transition-transform" />
                  <span className="font-semibold">224-374-7400</span>
                </a>
              </li>
              <li>
                <a href="mailto:contact@topfleetlogistics.com" className="flex items-center gap-3 text-slate-400 hover:text-white transition-colors group" data-testid="footer-email">
                  <Mail className="w-4 h-4 text-brand-blue group-hover:scale-110 transition-transform" />
                  <span className="text-sm">contact@topfleetlogistics.com</span>
                </a>
              </li>
              <li>
                <a href="mailto:operations@topfleetlogistics.com" className="flex items-center gap-3 text-slate-500 hover:text-white transition-colors group" data-testid="footer-operations-email">
                  <Mail className="w-4 h-4 text-slate-500 group-hover:scale-110 group-hover:text-brand-blue transition-all" />
                  <span className="text-xs">operations@topfleetlogistics.com</span>
                </a>
              </li>
              <li className="flex items-start gap-3 text-slate-400 text-sm">
                <MapPin className="w-4 h-4 text-brand-red flex-shrink-0 mt-0.5" />
                <span>Augusta, Georgia</span>
              </li>
            </ul>
            <p className="text-brand-red/60 text-xs mt-4 font-medium">We respond within 1 hour</p>
          </div>
        </div>

        {/* Guarantee + Divider */}
        <div className="text-center mt-12 mb-8">
          <p className="text-slate-500 text-sm" data-testid="footer-guarantee">
            Backed by the <span style={{ color: '#DC2626' }}>TopFleet</span> Service Guarantee
          </p>
        </div>
        <div className="section-divider my-8" />

        {/* Bottom */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex flex-col sm:flex-row items-center gap-x-4 gap-y-2">
            <p className="text-slate-500 text-sm">
              &copy; {currentYear} <span style={{ color: '#DC2626' }}>Top</span><span className="text-white">Fleet</span> Logistics. All rights reserved.
            </p>
            <div className="flex items-center gap-3 text-sm" data-testid="footer-legal-links">
              <span className="text-slate-700 hidden sm:inline">&middot;</span>
              <Link to="/legal" className="text-slate-500 hover:text-slate-300 transition-colors" data-testid="footer-legal-bottom-link">Legal & Policies</Link>
            </div>
          </div>
          <div className="flex items-center gap-4" data-testid="social-links">
            {[
              { href: 'https://facebook.com', icon: Facebook, label: 'Facebook', color: 'brand-blue' },
              { href: 'https://instagram.com', icon: Instagram, label: 'Instagram', color: 'brand-red' },
              { href: 'https://linkedin.com', icon: Linkedin, label: 'LinkedIn', color: 'brand-blue' },
            ].map(social => (
              <a key={social.label} href={social.href} target="_blank" rel="noopener noreferrer"
                className={`w-9 h-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-slate-400 hover:text-white hover:border-${social.color} transition-all`}
                data-testid={`social-${social.label.toLowerCase()}`} aria-label={social.label}>
                <social.icon className="w-4 h-4" />
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
